/**
 * coverLetterPdf.js
 * ------------------
 * Renders a saved cover-letter draft into a print-ready PDF via jsPDF.
 *
 * Design notes:
 * - A5-ish typographic feel: 1-inch margins, 11pt Helvetica body, generous
 *   line spacing (leading ≈ 1.55). Recruiters open the PDF, it should read
 *   like a real cover letter, not a memo.
 * - Header runs a small "Cover letter · {ROLE}" caption in muted grey so the
 *   file identifies itself at a glance. Footer stamps the tone + word count.
 * - Handles overflow via jsPDF's `splitTextToSize` and automatic `addPage()`
 *   so long drafts spill onto a second sheet without truncation.
 */

import { jsPDF } from 'jspdf';

const PAGE = { width: 210, height: 297 }; // A4 in mm
const MARGIN = { top: 22, bottom: 22, left: 22, right: 22 };
const BODY_WIDTH = PAGE.width - MARGIN.left - MARGIN.right;

function toneLabel(tone) {
  const map = { formal: 'Formal', warm: 'Warm', bold: 'Bold', custom: 'Custom' };
  return map[tone] || 'Formal';
}

/** Build the jsPDF document without saving/downloading. Returned instance can
 *  be `.save()`-d or turned into a Blob for embedding elsewhere. */
export function buildCoverLetterPdf(letter) {
  const {
    role_title = 'Cover letter',
    role_sector = '',
    tone = 'formal',
    tone_hint = '',
    opener = 'Dear Hiring Manager,',
    body = '',
    closer = 'With appreciation,',
    word_count = 0,
    candidate = null, // { name, title, email, location, linkedin }
  } = letter || {};

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });

  // --- Candidate cover-sheet header -----------------------------------------
  // Recruiters open the PDF; the first thing they should see is who this is,
  // not the mechanical "Cover Letter" caption. Name → title (if any) → contact
  // line, then a full-width divider, THEN the caption row.
  const headerLeft = MARGIN.left;
  const headerRight = PAGE.width - MARGIN.right;
  let headerY = MARGIN.top - 6; // start above the standard body top

  const cand = candidate || {};
  const hasCandidate = Boolean(cand.name || cand.email || cand.title || cand.location);

  if (hasCandidate) {
    // Name — big, bold, sd-black
    if (cand.name) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(15);
      doc.setTextColor(20, 20, 20);
      doc.text(cand.name.trim(), headerLeft, headerY);
      headerY += 5.5;
    }
    // Optional job title on its own line — treat as a sub-headline
    if (cand.title) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(90, 90, 90);
      doc.text(cand.title.trim(), headerLeft, headerY);
      headerY += 4.5;
    }
    // Contact line — email · location · linkedin, only the parts we have
    const contactParts = [];
    if (cand.email) contactParts.push(cand.email.trim());
    if (cand.location) contactParts.push(cand.location.trim());
    if (cand.linkedin) contactParts.push(cand.linkedin.replace(/^https?:\/\//, '').trim());
    if (contactParts.length) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text(contactParts.join('  ·  '), headerLeft, headerY);
      headerY += 4;
    }
    // Thin divider
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.3);
    doc.line(headerLeft, headerY, headerRight, headerY);
    headerY += 4;
  }

  // --- Caption row (COVER LETTER · role) ------------------------------------
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(140, 140, 140);
  const captionParts = ['COVER LETTER', role_title];
  if (role_sector) captionParts.push(role_sector);
  doc.text(captionParts.join('  ·  ').toUpperCase(), headerLeft, headerY);
  headerY += 4;

  // --- Body typography setup -------------------------------------------------
  const LEADING = 6.4; // ≈ 1.5 line-height at 11pt
  let y = Math.max(headerY + 2, MARGIN.top);

  const writeParagraph = (text, opts = {}) => {
    if (!text) return;
    const { bold = false, gap = LEADING * 0.9, size = 11, color = [30, 30, 30] } = opts;
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    doc.setTextColor(color[0], color[1], color[2]);
    const lines = doc.splitTextToSize(text, BODY_WIDTH);
    for (const line of lines) {
      // Page break if we're about to fall past the bottom margin
      if (y + LEADING > PAGE.height - MARGIN.bottom) {
        doc.addPage();
        y = MARGIN.top;
      }
      doc.text(line, MARGIN.left, y);
      y += LEADING;
    }
    y += gap;
  };

  // Opener — slightly bold so it reads like a salutation
  writeParagraph(opener, { bold: true, gap: LEADING * 1.1 });

  // Body — split on blank lines so paragraph rhythm is preserved
  const paragraphs = (body || '').split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  for (const p of paragraphs) writeParagraph(p);

  // Closer — bold sign-off
  writeParagraph(closer, { bold: true, gap: LEADING * 0.6 });

  // --- Footer ---------------------------------------------------------------
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i += 1) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(160, 160, 160);
    const meta = [`Tone: ${toneLabel(tone)}`];
    if (tone === 'custom' && tone_hint) meta.push(`Hint: "${tone_hint.slice(0, 60)}${tone_hint.length > 60 ? '…' : ''}"`);
    if (word_count) meta.push(`${word_count} words`);
    doc.text(meta.join('  ·  '), MARGIN.left, PAGE.height - 12);
    doc.text(`${i} / ${totalPages}`, PAGE.width - MARGIN.right, PAGE.height - 12, { align: 'right' });
  }

  return doc;
}

/** Convenience — build + trigger a browser download. Returns the filename. */
export function downloadCoverLetterPdf(letter) {
  const doc = buildCoverLetterPdf(letter);
  const safe = (letter?.role_title || 'cover-letter')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'cover-letter';
  const filename = `cover-letter-${safe}-${letter?.tone || 'formal'}.pdf`;
  doc.save(filename);
  return filename;
}

/** Build + open the user's mail client with the letter body pre-filled and an
 *  attach reminder pointing at the just-downloaded PDF. Returns the filename
 *  so callers can show a toast with the exact name.
 */
export function emailCoverLetterAsPdf(letter) {
  const filename = downloadCoverLetterPdf(letter);
  const subject = `Cover letter — ${letter?.role_title || 'Application'}`;
  const opener = letter?.opener || 'Dear Hiring Manager,';
  const body = letter?.body || '';
  const closer = letter?.closer || 'With appreciation,';
  let plain = `${opener}\n\n${body}\n\n${closer}`;
  // Keep the mailto URL within safe limits — the PDF is the real payload.
  if (plain.length > 1400) plain = plain.slice(0, 1400) + '\n\n[Trimmed — the full letter is in the attached PDF.]';
  const wrapper =
`Hi,

Please find my cover letter attached as a PDF (file: ${filename}).

Just in case, here it is inline as well:

──────────────────────────────
${plain}
──────────────────────────────

Attach the PDF from your Downloads folder before sending.`;
  const href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(wrapper)}`;
  // Small delay so the browser has time to kick off the file download before
  // navigating away — Safari especially can drop the download otherwise.
  setTimeout(() => { window.location.href = href; }, 120);
  return filename;
}
